// import { TableStructure } from '~components/TableComponent/types';
import { defineStore } from 'pinia';
import Api, { Payload } from '~utils/Api';
import { modules } from '@/constants';
import { useMainStore } from '~store/index';
import { useAuthStore } from '~store/auth/authModule';
import {
  Chapter,
  Answer,
  Slot,
  Appointment,
  Branch,
  UserDocument,
  GoskluchSignStatus,
  GoskluchSignType,
  GoskluchSignResult,
} from './types';
// const api = new Api();

export const useEcoHospitalStore = defineStore('ecohospital', {
  state: () => ({
    poll: [] as Chapter[],
    branches: [] as Branch[],
    documents: [] as UserDocument[],
    signStatuses: [] as GoskluchSignStatus[],
    updating: false,
  }),
  getters: {
    parsedBranches: (state) => state.branches.map((i) => ({
      ...i,
      buildingCoords: JSON.parse(i.buildingCoords),
      entranceCoords: JSON.parse(i.entranceCoords),
    })),
  },
  actions: {
    async getPoll(): Promise<void> {
      const params: Payload = {
        Name_event: 'GET:poll_questions',
        Name_module: modules.eco,
        id_poll: useAuthStore().user.show_poll,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        this.poll = JSON.parse(result.data?.[0]?.chapters ?? []);
      }
    },
    async getAppointmenCalendar(): Promise<Slot[]> {
      this.updating = true;
      try {
        const params: Payload = {
          Name_event: 'GET:calendar',
          Name_module: modules.eco,
          ...useMainStore().getConstantParams,
        };
        const result = await Api.get(params);
        if (!result.errorType) {
          return JSON.parse(result.data?.[0].result) ?? [];
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.updating = false;
      }
      return [];
    },
    async getDocuments(): Promise<any> {
      const params: Payload = {
        Name_event: 'GET:list_load',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const [result] = await Promise.all([
        Api.get(params),
        this.getSignStatuses(),
      ]);
      if (!result.errorType) {
        this.documents = result.data as UserDocument[] ?? [];
      }
      return [];
    },
    async getSignStatuses(): Promise<GoskluchSignStatus[]> {
      const params: Payload = {
        Name_event: 'GET:sign_docs_status_GK',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        this.signStatuses = result.data as GoskluchSignStatus[] ?? [];
      }
      return this.signStatuses;
    },
    async getFilePath(id_document: number): Promise<any> {
      const params: Payload = {
        Name_event: 'GET:file_path',
        Name_module: modules.eco,
        id_document,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        return result.data;
      }
      return false;
    },
    async getBranches(): Promise<any> {
      const params: Payload = {
        Name_event: 'GET:branches',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        this.branches = result.data as Branch[] ?? [];
      }
      return false;
    },
    async getDesignList(): Promise<any> {
      const params: Payload = {
        Name_event: 'GET:design_list',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        return result.data;
      }
      return false;
    },
    async cancelDesign(id_design: number): Promise<any> {
      const params = {
        Name_event: 'POST:design_cancel',
        Name_module: modules.eco,
        id_design,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.post(params, true);
      if (!result.errorType) {
        await this.getDesignList();
        return true;
      } return null;
    },
    async sendChaptersAnswers(context: Answer): Promise<any> {
      const params = {
        Name_event: 'POST:poll_answers',
        Name_module: modules.eco,
        id_page: context.page,
        id_poll: useAuthStore().user.show_poll,
        json_params: JSON.stringify(context.chapter),
        ...useMainStore().getConstantParams,
      };
      const result = await Api.post(params, true);
      if (!result.errorType) {
        await this.getPoll();
        return true;
      } return null;
    },
    async createAppointment(context: Appointment): Promise<any> {
      const params = {
        Name_event: 'POST:setup_appointment',
        Name_module: modules.eco,
        json_params: JSON.stringify({
          date: context.date,
          time: context.time,
          tel: context.tel,
        }),
        ...useMainStore().getConstantParams,
      };
      const result = await Api.post(params, true);
      if (!result.errorType) {
        this.getAppointmenCalendar();
        return result.data;
      } return null;
    },
    async createPollFile(): Promise<any> {
      const params = {
        Name_event: 'POST:gen_poll6',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.post(params, true);
      if (!result.errorType) {
        await this.getDocuments();
        return true;
      } return null;
    },

    async isUnepAllowed(): Promise<boolean> {
      const params: Payload = {
        Name_event: 'GET:is_UNEP_allowed',
        Name_module: modules.eco,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (result.errorType) return false;
      // TODO: сверить имя поля с ответом бэка
      return result.data?.[0]?.UNEP_allowed === true;
    },
    async signGoskluchDocument(
      id_document: number,
      type_sign: GoskluchSignType = 1,
    ): Promise<GoskluchSignResult> {
      const params = {
        Name_event: 'POST:sign_doc_by_GK',
        Name_module: modules.eco,
        id_document,
        type_sign,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.post(params, true);
      const err = result.data?.[0]?.err ?? null;
      return {
        ok: !result.errorType && err === 0,
        err,
      };
    },
  },
});
